<template>
  <v-dialog v-model="showDialog" max-width="500">
    <v-card>
      <v-card-title>Create New Group</v-card-title>
      <v-card-text>
        <v-form ref="groupForm" v-model="groupValid" @submit.prevent="createGroup">
          <v-text-field
            v-model="newGroup.name"
            label="Name"
            :rules="[v => !!v || 'Name is required']"
            required
            @keydown.enter.prevent="createGroup"
          ></v-text-field>
          <v-textarea
            v-model="newGroup.description"
            label="Description (optional)"
            rows="2"
            @keydown.enter.prevent="createGroup"
          ></v-textarea>
          <v-text-field
            v-model="newGroup.color"
            label="Color (optional)"
            placeholder="#FF5722 or red"
            @keydown.enter.prevent="createGroup"
          ></v-text-field>
          
          <!-- AI Image Generation -->
          <v-alert
            v-if="imageGenerationStatusLoaded && !imageGenerationAvailable"
            type="info"
            variant="tonal"
            density="compact"
            class="mt-2"
          >
            {{ imageGenerationGuidance }}
          </v-alert>
          <v-checkbox
            v-model="newGroup.generateImage"
            label="Generate AI image for this group (generated when saving)"
            density="compact"
            class="mt-2"
            :disabled="!imageGenerationAvailable"
            :hint="imageGenerationAvailable ? 'The configured provider will generate the image when saving.' : imageGenerationGuidance"
            persistent-hint
          ></v-checkbox>
          
          <v-textarea
            v-if="newGroup.generateImage"
            v-model="newGroup.imagePrompt"
            label="Image generation prompt"
            rows="3"
            variant="outlined"
            class="mt-2"
            hint="This prompt will be used to generate an AI image for the group"
            persistent-hint
          ></v-textarea>
        </v-form>
      </v-card-text>
      <v-card-actions>
        <div v-if="isCreatingGroup && newGroup.generateImage" class="d-flex align-center text-caption text-grey ml-4">
          <v-progress-circular size="16" width="2" indeterminate class="mr-2"></v-progress-circular>
          Generating AI image, please wait...
        </div>
        <v-spacer></v-spacer>
        <v-btn @click="cancelGroupCreate" :disabled="isCreatingGroup">Cancel</v-btn>
        <v-btn 
          ref="createGroupButton" 
          color="primary" 
          @click="createGroup" 
          :disabled="!groupValid || isCreatingGroup"
          :loading="isCreatingGroup"
        >
          Create
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { ref, computed, watch, nextTick, onMounted } from 'vue'
import { useListStore } from '../stores/list'
import { useGroupStore } from '../stores/group'
import { useAuthStore } from '../stores/auth'
import { useImageGenerationConfig } from '../composables/useImageGenerationConfig'

export default {
  name: 'AddGroupDialog',
  props: {
    modelValue: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:modelValue', 'created', 'message'],
  setup(props, { emit }) {
    const listStore = useListStore()
    const groupStore = useGroupStore()
    const authStore = useAuthStore()
    const {
      imageGenerationAvailable,
      imageGenerationStatusLoaded,
      loadImageGenerationStatus
    } = useImageGenerationConfig()

    const imageGenerationGuidance = computed(() => (
      authStore.user?.role === 'ADMIN'
        ? 'Configure and enable an image provider in the Admin Panel.'
        : 'Image generation is not configured. Ask an administrator to enable it.'
    ))

    const showDialog = computed({
      get() {
        return props.modelValue
      },
      set(value) {
        emit('update:modelValue', value)
      }
    })

    const groupForm = ref(null)
    const createGroupButton = ref(null)
    const groupValid = ref(false)
    const isCreatingGroup = ref(false)

    const newGroup = ref({
      name: '',
      description: '',
      color: '',
      generateImage: false,
      imagePrompt: ''
    })

    const createGroup = async () => {
      if (!groupValid.value || !listStore.currentList) return
      
      isCreatingGroup.value = true
      
      const groupData = {
        name: newGroup.value.name,
        description: newGroup.value.description,
        color: newGroup.value.color,
        packlist_id: listStore.currentList.id,
        generate_image: newGroup.value.generateImage,
        image_prompt: newGroup.value.imagePrompt
      }
      
      try {
        const result = await groupStore.createGroup(groupData)
        if (result.success) {
          let message = 'Group created successfully!'
          
          // Check if image was generated
          if (newGroup.value.generateImage && result.group.image_url) {
            message = 'Group created and AI image generated successfully!'
          } else if (newGroup.value.generateImage && !result.group.image_url) {
            message = 'Group created, but image generation failed'
          }
          
          emit('message', { message, color: 'success' })
          emit('created')
          cancelGroupCreate()
        } else {
          emit('message', { message: result.error, color: 'error' })
        }
      } finally {
        isCreatingGroup.value = false
      }
    }

    const cancelGroupCreate = () => {
      newGroup.value = { 
        name: '', 
        description: '', 
        color: '', 
        generateImage: false, 
        imagePrompt: '' 
      }
      showDialog.value = false
    }

    // Watch for generateImage checkbox to set default prompt
    watch(() => newGroup.value.generateImage, (newVal) => {
      if (newVal && !newGroup.value.imagePrompt.trim()) {
        const listName = listStore.currentList?.name || 'list'
        const groupName = newGroup.value.name || 'group'
        newGroup.value.imagePrompt = `${listName}: ${groupName}`
      }
    })

    // Update prompt when group name changes if image generation is enabled
    watch(() => newGroup.value.name, (newVal) => {
      if (newGroup.value.generateImage && newVal) {
        const listName = listStore.currentList?.name || 'list'
        newGroup.value.imagePrompt = `${listName}: ${newVal}`
      }
    })

    // Focus create button when dialog opens
    watch(showDialog, (newVal) => {
      if (newVal) {
        loadImageGenerationStatus(true).catch(() => {})
        nextTick(() => {
          if (createGroupButton.value?.$el) {
            createGroupButton.value.$el.focus()
          }
        })
      }
    })

    watch(imageGenerationAvailable, (available) => {
      if (!available) newGroup.value.generateImage = false
    })

    onMounted(() => {
      loadImageGenerationStatus().catch(() => {})
    })

    return {
      showDialog,
      groupForm,
      createGroupButton,
      groupValid,
      isCreatingGroup,
      imageGenerationAvailable,
      imageGenerationStatusLoaded,
      imageGenerationGuidance,
      newGroup,
      createGroup,
      cancelGroupCreate
    }
  }
}
</script>
